import { z } from "zod";
import {
  AiKeyStatus,
  BillingModel,
  Count,
  CustomerStatus,
  EntitlementKind,
  HttpsUrl,
  IsoDate,
  IsoDateTime,
  LEDGER_CURRENCY,
  LedgerType,
  Money,
  NonNegativeMoney,
  OrderStatus,
  paged,
  PageQuery,
  Period,
  PositiveMoney,
  ProviderType,
  Reason,
  TaskKind,
  TaskStatus,
  Uuid,
} from "./common.ts";
import { InstanceHost } from "./auth.ts";
import { ProductFamily } from "./catalog.ts";
import { Customer, CustomerUser } from "./customer.ts";
import { Entitlement, MonthlyLimitRequest, MonthlyLimitResponse } from "./entitlements.ts";
import { ActivityEvent } from "./history.ts";
import { Order, OrderProduct } from "./orders.ts";
import { actionsOf } from "./permissions.ts";
import { LedgerEntry, LiveUsage, MonthBudget, UsageReport } from "./usage.ts";

/*
 * The staff area, `/api/staff/*` (07 §2.2, 01 §5, UC-04, UC-05, UC-08, UC-11, UC-13 to UC-16,
 * UC-30). Every action carries the staff member as actor and, where 07 demands it, a reason
 * that lands in the customer's timeline (REQ-12, R-11). Routes marked "als Skript" in 07 are
 * commands in `apps/api` in the alpha release and have no contract here yet.
 */

// ── customers ────────────────────────────────────────────────────────────────────

/** One row of the customer list. */
export const StaffCustomerSummary = z.object({
  id: Uuid,
  name: z.string(),
  status: CustomerStatus,
  instanceHost: InstanceHost.nullable(),
  creditLimit: NonNegativeMoney.nullable(),
  /** sum of billable ledger entries, across periods */
  openAmount: Money,
  userCount: Count,
  openTaskCount: Count,
  createdAt: IsoDateTime,
});
export type StaffCustomerSummary = z.infer<typeof StaffCustomerSummary>;

/** `GET /api/staff/customers?q=` searches name and instance host. */
export const StaffCustomerListQuery = PageQuery.extend({
  q: z.string().trim().max(200).optional(),
  status: CustomerStatus.optional(),
});
export type StaffCustomerListQuery = z.infer<typeof StaffCustomerListQuery>;

export const StaffCustomerListResponse = paged(StaffCustomerSummary);
export type StaffCustomerListResponse = z.infer<typeof StaffCustomerListResponse>;

/**
 * The customer with the fields only staff see. Every route under `/api/staff` answers an actor
 * without a staff role with `forbidden` and `role_required` (AP-B8); a customer that does not
 * exist is `not_found`.
 */
export const StaffCustomer = Customer.extend({
  approvedBy: Uuid.nullable(),
  /** when the customer's data processing agreement (AVV) was in place, recorded with the approval */
  dpaAcceptedAt: IsoDateTime.nullable(),
  suspendedReason: z.string().nullable(),
  /**
   * ADR-075; reasons that occur: `customer_already_approved`, `customer_suspended`. For
   * `customer.suspend` (AP-B8.5) the action is allowed on an approved customer (suspend) and on a
   * suspended one (reinstate); the direction is the route's, a refusal of the wrong one comes
   * after submitting (`customer_already_suspended`, `customer_not_suspended`). `order.create`
   * (UC-32, ADR-133) knows `customer_suspended` only; what depends on the product comes after
   * submitting (`demo_only`, `entitlement_exists`, `order_open`)
   */
  actions: actionsOf("customer.approve", "credit_limit.set", "customer.suspend", "instance.change", "order.create"),
});
export type StaffCustomer = z.infer<typeof StaffCustomer>;

/** The AI key behind an entitlement, without the secret (R-13): state and its limit at OpenRouter. */
export const AiKeySummary = z.object({
  id: Uuid,
  status: AiKeyStatus,
  /**
   * the limit at OpenRouter as last pushed: the effective limit, zero while the customer is
   * suspended, else the monthly limit less the markup (H2, RN6, TL 2026-10-30); not the monthly
   * limit itself, and a cost without markup, so nothing to show as an amount. Null while the state
   * at OpenRouter is unknown, after a `limit.push` failed for good; the next push sets it (GW1)
   */
  limit: NonNegativeMoney.nullable(),
  expiresAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type AiKeySummary = z.infer<typeof AiKeySummary>;

/**
 * The entitlement as staff see it, without `maxMonthlyLimit`, the ceiling of the customer's limit
 * dialog (AP-B14-09): the team sets a customer's monthly limit too (ADR-160 point F12.4-3,
 * `PUT /api/staff/entitlements/:id/monthly-limit`), but its dialog in the file shows no ceiling
 * before submitting; a refusal names credit limit and the other limits (AP-B14-13, AP-F12-54).
 */
export const StaffEntitlement = Entitlement.omit({ maxMonthlyLimit: true }).extend({
  /** the monthly limit, always: staff see the amounts the customer's user does not (AP-B14-12) */
  monthlyLimit: NonNegativeMoney,
  key: AiKeySummary.nullable(),
  /**
   * the running month at OpenRouter for the active key, as the customer's usage view reads it
   * (ADR-121 points 2 and 3, ADR-145 point 2): null without an active key or without an answer in
   * time; never booked
   */
  live: LiveUsage.nullable(),
  /**
   * booked through this UTC day (ADR-145 point 2): the earliest watermark of the keys the daily
   * fetch still reads (active ones, revoked ones until their day of revocation is booked; an empty
   * watermark counts as the day before the key was created, ADR-120). With no such key the latest
   * watermark of its keys, everything is booked then; null without any key
   */
  usageFinalThrough: IsoDate.nullable(),
  /**
   * the available amount (ADR-149): the monthly limit minus `live.amount`; null while `live` or its
   * amount is null; negative as it is, after a lowered limit for one
   */
  available: Money.nullable(),
  /**
   * ADR-075; `limit.set` decides the team's `PUT /api/staff/entitlements/:id/monthly-limit` by the
   * rules of the customer's route (ADR-160 point F12.4-3); `key.reveal` is refused with
   * `key_missing` while no key is active, `entitlement.revoke` (UC-17 by hand, AP-B8.5) and
   * `entitlement.terminate` (UC-16, ADR-137) with `entitlement_not_live` on an ended one
   */
  actions: actionsOf("limit.set", "key.reveal", "key.rotate", "entitlement.revoke", "entitlement.terminate"),
});
export type StaffEntitlement = z.infer<typeof StaffEntitlement>;

/** A manual task, a provisioning step a staff member completes (06 `manual_task`, R-15). */
export const ManualTask = z.object({
  id: Uuid,
  customerId: Uuid,
  /** the customer's name, like `StaffOrder`, so the task list names the customer (ADR-101) */
  customerName: z.string(),
  entitlementId: Uuid.nullable(),
  orderId: Uuid.nullable(),
  /**
   * the product the task is about, the entitlement's or else the order's, so the task names it
   * (C-48, like `customerName`); null for a task without either
   */
  product: OrderProduct.nullable(),
  /**
   * the provider of the task's entitlement, its order's or else its product's; it pre-fills the
   * resource's kind at "Einrichtung erledigen" (ADR-144 point 6). Null together with `product`
   */
  provider: ProviderType.nullable(),
  kind: TaskKind,
  status: TaskStatus,
  assignedTo: Uuid.nullable(),
  note: z.string().nullable(),
  dueAt: IsoDateTime.nullable(),
  doneAt: IsoDateTime.nullable(),
  doneBy: Uuid.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** ADR-075; `task_done` once the task is done or cancelled */
  actions: actionsOf("task.complete"),
});
export type ManualTask = z.infer<typeof ManualTask>;

/** An order as staff see it, with the customer's name and the processing decision (ADR-075). */
export const StaffOrder = Order.extend({
  customerName: z.string(),
  /** ADR-075; `order_closed` once completed or cancelled */
  actions: actionsOf("order.process"),
});
export type StaffOrder = z.infer<typeof StaffOrder>;

/**
 * A user of the customer as staff see them (UC-21, AP-B8.5): the membership plus the decision for
 * setting the role by hand. The action hangs on the role alone; a manual one stays until support
 * lifts it (ADR-041), which the same route does with `role: null`. `lastLoginAt` is the user's
 * last login at the platform, null before the first; staff only, the customer's own list does not
 * carry it (TL 2026-10-23).
 */
export const StaffCustomerUser = CustomerUser.extend({
  lastLoginAt: IsoDateTime.nullable(),
  actions: actionsOf("role.set"),
});
export type StaffCustomerUser = z.infer<typeof StaffCustomerUser>;

/**
 * The figures at the head of the customer file (UC-15, C-30), counted by the API as the customer
 * list counts them; the portal only shows them. With the month's budget the customer sees
 * (`MonthBudget`, ADR-158 point F12.2-10 sub-point 4, F12.A-1): `live`, `available` and the budget
 * are the running month's summary's (`UsageSummaryResponse`), read by the same function, so support
 * sees why the customer's capsule is red (BB5).
 */
export const StaffCustomerFigures = MonthBudget.extend({
  /** the monthly limits of the living entitlements (pending, active, paused), the sum R-04 holds against the credit limit */
  monthlyLimitSum: NonNegativeMoney,
  /** sum of billable ledger entries, across periods, as `StaffCustomerSummary.openAmount` */
  openAmount: Money,
  userCount: Count,
  openTaskCount: Count,
  /**
   * the running month's live usage of the whole customer, the summary's `live`: the sum over the
   * summary's entitlements with an active key, `asOf` the oldest reading among them; `amount` null
   * while one reading has none; null as soon as one of those keys gives no reading (time limit, a
   * key OpenRouter does not know), and when none has a key (ADR-145 point 3, addendum of
   * 2026-11-04, BB1)
   */
  live: LiveUsage.nullable(),
  /**
   * booked through this UTC day for the whole customer: the earliest watermark over the keys the
   * fetch still reads, by the rule of `StaffEntitlement.usageFinalThrough` (ADR-145 point 3)
   */
  usageFinalThrough: IsoDate.nullable(),
  /**
   * the available amount of the whole customer (ADR-149): `monthlyLimitSum` minus `live.amount`;
   * null while `live` or its amount is null; negative as it is. Not a margin against the credit
   * limit, R-04 holds the limit sum
   */
  available: Money.nullable(),
});
export type StaffCustomerFigures = z.infer<typeof StaffCustomerFigures>;

/**
 * `GET /api/staff/customers/:id` (UC-15): the customer with its figures, timeline, users,
 * entitlements with provisioning state and key state, orders with their processing decision, and
 * open tasks. The timeline is the first page, newest first; the full one is paged through
 * `GET /api/staff/customers/:id/timeline`.
 */
export const StaffCustomerDetail = z.object({
  customer: StaffCustomer,
  figures: StaffCustomerFigures,
  users: z.array(StaffCustomerUser),
  entitlements: z.array(StaffEntitlement),
  orders: z.array(StaffOrder),
  tasks: z.array(ManualTask),
  timeline: paged(ActivityEvent),
});
export type StaffCustomerDetail = z.infer<typeof StaffCustomerDetail>;

export const StaffTimelineQuery = PageQuery;
export const StaffTimelineResponse = paged(ActivityEvent);

/** A credit limit is in the ledger currency, USD, like the monthly limits it bounds (ADR-058). */
const inLedgerCurrency = (money: { currency: string }) => money.currency === LEDGER_CURRENCY;
/** credit limit, monthly limits, a correction: every amount of the ledger is in its currency */
const ledgerCurrencyIssue = {
  message: `the amount is in ${LEDGER_CURRENCY}, the ledger's currency (ADR-058)`,
  path: ["currency"],
};

/**
 * `POST /api/staff/customers/:id/approve` (UC-05). Approval needs a credit limit above zero in
 * USD (R-03), the time the customer's AVV was in place, which cannot lie in the future, and a
 * reason that goes to the timeline with the staff member as actor (REQ-12). Rejected with
 * `conflict` and `customer_already_approved`, with `forbidden` and `customer_suspended` (the
 * status follows the reason, ADR-085), and with `limit_exceeded` and `credit_limit_exceeded`
 * when living monthly limits already add up to more (R-04).
 */
export const ApproveRequest = z.object({
  creditLimit: PositiveMoney.refine(inLedgerCurrency, ledgerCurrencyIssue),
  dpaAcceptedAt: IsoDateTime.refine((value) => Date.parse(value) <= Date.now(), {
    message: "the AVV cannot be in place in the future",
  }),
  reason: Reason,
});
export type ApproveRequest = z.infer<typeof ApproveRequest>;

/**
 * `PUT /api/staff/customers/:id/credit-limit` (UC-11), in USD. Lowering below the sum of the
 * monthly limits is rejected with `limit_exceeded` and reason `credit_limit_exceeded` until the
 * customer lowered limits (F-46 open); `details` is `CreditLimitExceededDetails` with the sum of
 * all living limits as `otherLimits`. Rejected with `forbidden` and `customer_not_approved` or
 * `customer_suspended` for a customer that has no credit limit to change. An unchanged credit
 * limit writes nothing.
 */
export const CreditLimitRequest = z.object({
  creditLimit: NonNegativeMoney.refine(inLedgerCurrency, ledgerCurrencyIssue),
  reason: Reason,
});
export type CreditLimitRequest = z.infer<typeof CreditLimitRequest>;

/** Both return the customer as staff see it. */
export const StaffCustomerResponse = StaffCustomer;

/**
 * `POST /api/staff/customers` (UC-20, ADR-133 point 4), answered with `201` and the customer as
 * staff see it. Support and admin create a customer after the deal outside the platform: the
 * organization's name (not unique), the host of its dataCycle instance or `null` when it is not
 * set up yet, and an optional reason for the timeline, which the customer sees too (ADR-117). The
 * customer starts `registered` in USD without credit limit; approval stays UC-05. A host follows
 * the rules of the instance link (`CustomerInstanceRequest`): outside the login's allowlist
 * `forbidden` with `instance_mismatch` (ADR-126), another customer's `conflict` with
 * `instance_in_use` (R-17); then nothing is created. Without a host the team links the instance
 * later. The first user who logs in over the linked instance joins this customer (UC-22).
 */
export const StaffCustomerCreateRequest = z.object({
  name: z.string().trim().min(1).max(200),
  host: InstanceHost.nullable(),
  reason: Reason.optional(),
});
export type StaffCustomerCreateRequest = z.infer<typeof StaffCustomerCreateRequest>;

/**
 * `POST /api/staff/customers/:id/suspend` (UC-18, AP-B8.5). Suspends an approved customer with a
 * mandatory reason, which goes to the timeline with the staff member as actor (REQ-12); the keys'
 * monthly limits go to zero at OpenRouter and the customer can neither use AI nor order. Rejected
 * for a registered customer with `forbidden` and `customer_not_approved`, for a suspended one
 * with `conflict` and `customer_already_suspended` (ADR-085).
 */
export const CustomerSuspendRequest = z.object({
  reason: Reason,
});
export type CustomerSuspendRequest = z.infer<typeof CustomerSuspendRequest>;

/**
 * `POST /api/staff/customers/:id/reinstate` (UC-18, AP-B8.5). Reinstates a suspended customer with
 * a mandatory reason; the keys' monthly limits go back to the entitlements' limits at OpenRouter.
 * Rejected for a customer that is not suspended with `conflict` and `customer_not_suspended`
 * (ADR-085).
 */
export const CustomerReinstateRequest = z.object({
  reason: Reason,
});
export type CustomerReinstateRequest = z.infer<typeof CustomerReinstateRequest>;

/**
 * `PUT /api/staff/customers/:id/instance` (UC-22 "Änderungen durch das Team", AP-B8.5). Links the
 * customer to another instance, or resolves the link with `host: null`; re-linking is the same
 * route later. The host must be a bare hostname, normalized like the login's (`InstanceHost`), and
 * must not belong to another customer (`conflict`, `instance_in_use`, R-17); the login's allowlist
 * applies as well (`forbidden`, `instance_mismatch`, ADR-126). The members' belonging is
 * re-checked at their next login (ADR-062), manual roles stay (ADR-041).
 */
export const CustomerInstanceRequest = z.object({
  host: InstanceHost.nullable(),
  reason: Reason,
});
export type CustomerInstanceRequest = z.infer<typeof CustomerInstanceRequest>;

/**
 * `PUT /api/staff/customers/:id/users/:userId/role` (UC-21, AP-B8.5). Sets a member's role by
 * hand (ADR-041) with a mandatory reason for the timeline, or lifts a manual role back to the
 * instance's mapping with `role: null`. An unchanged combination writes nothing. Rejected for a
 * user without a living membership at the customer with `404` (K-05).
 */
export const StaffUserRoleRequest = z.object({
  role: z.enum(["administrator", "user"]).nullable(),
  reason: Reason,
});
export type StaffUserRoleRequest = z.infer<typeof StaffUserRoleRequest>;

// ── orders ───────────────────────────────────────────────────────────────────────

/** `GET /api/staff/orders`, newest first; without `status` the open ones. */
export const StaffOrderListQuery = PageQuery.extend({
  status: OrderStatus.optional(),
  customerId: Uuid.optional(),
});
export type StaffOrderListQuery = z.infer<typeof StaffOrderListQuery>;

export const StaffOrderListResponse = paged(StaffOrder);
export type StaffOrderListResponse = z.infer<typeof StaffOrderListResponse>;

/**
 * `POST /api/staff/customers/:id/orders` (UC-32, ADR-133 point 5), answered with `201` and the
 * order as staff see it. Support and admin order any product the catalogue shows for the customer,
 * one on request too; a product the catalogue does not show is `404`. Neither the switch of the
 * customer's own ordering, nor "on request", nor the approval is checked (R-02: ordering needs
 * none, the processing does). The order is always `standard`, `placedBy` stays null, and the
 * optional reason goes to the timeline with the staff member as actor, visible to the customer
 * (ADR-117). Rejected with `forbidden` and `customer_suspended`, after submitting with `conflict`
 * and `demo_only`, `entitlement_exists` or `order_open` (R-01, ADR-029).
 */
export const StaffOrderCreateRequest = z.object({
  productId: Uuid,
  reason: Reason.optional(),
});
export type StaffOrderCreateRequest = z.infer<typeof StaffOrderCreateRequest>;

/**
 * `PUT /api/staff/orders/:id/status` (UC-04, UC-05, UC-08), forward only:
 *
 * - `in_progress` from `received` starts the provisioning: the entitlement of the order's kind as
 *   `pending` with the limit the order was placed with (the default or demo limit of the product
 *   when it was submitted, 0012, M3), and a `provision` task. Needs an approved customer
 *   (`customer_not_approved`, `customer_suspended`), no living entitlement for the product
 *   (`entitlement_exists`), a limit at the order (`demo_not_offered` for a demo of a product that
 *   offered none then) and room under the credit limit (`limit_exceeded`, R-04).
 * - `completed` from `in_progress` once the provisioning is done (`provisioning_open`) and, for an
 *   order placed with a price, the invoice reference is there (`invoice_reference_required`).
 * - `cancelled` from `received` or `in_progress` with a reason, which goes to the timeline (UC-05:
 *   a rejection); a living entitlement of the order is revoked, its open `provision` task is
 *   cancelled (a `remove` task stays, M2), a resource already provisioned gets a `remove` task.
 *
 * The same status again writes nothing. A closed order is refused with `order_closed`.
 */
export const OrderStatusRequest = z
  .object({
    status: z.enum(["in_progress", "completed", "cancelled"]),
    reason: Reason.optional(),
  })
  .refine((request) => request.status !== "cancelled" || request.reason !== undefined, {
    message: "a cancellation needs a reason",
    path: ["reason"],
  });
export type OrderStatusRequest = z.infer<typeof OrderStatusRequest>;

/**
 * `PUT /api/staff/orders/:id/invoice` (UC-04 step 4): the invoice number from outside (ADR-007),
 * on an open order; a second one replaces the first, both in the timeline.
 */
export const OrderInvoiceRequest = z.object({
  invoiceReference: z.string().trim().min(1).max(100),
});
export type OrderInvoiceRequest = z.infer<typeof OrderInvoiceRequest>;

// ── tasks ────────────────────────────────────────────────────────────────────────

/** `GET /api/staff/tasks`; without `status` the open ones. */
export const TaskListQuery = PageQuery.extend({
  status: TaskStatus.optional(),
  customerId: Uuid.optional(),
  kind: TaskKind.optional(),
});
export type TaskListQuery = z.infer<typeof TaskListQuery>;

export const TaskListResponse = paged(ManualTask);
export type TaskListResponse = z.infer<typeof TaskListResponse>;

/**
 * `POST /api/staff/tasks/:id/done` (UC-08, UC-24). A `provision` task is completed with the
 * resource it produced (`validation_failed` without); the entitlement becomes active, a demo's
 * period starts with the duration the order was placed with, and a demo without one is refused
 * with `409 demo_not_offered` (0012, M3). The job `key.create` follows in the same transaction
 * (08 §4). A `remove` task marks the resource removed. Other kinds take a note only. A task no
 * longer open is refused with `task_done`.
 */
export const TaskDoneRequest = z.object({
  resource: z
    .object({
      /** in the alpha release `ai_cockpit` */
      kind: z.string().min(1),
      externalRef: z.string().trim().max(200).nullable(),
      /** https only: the portal links it (M15 of the review 2026-10-19) */
      url: HttpsUrl.nullable(),
    })
    .optional(),
  note: Reason.optional(),
});
export type TaskDoneRequest = z.infer<typeof TaskDoneRequest>;

export const TaskDoneResponse = z.object({
  task: ManualTask,
  entitlement: StaffEntitlement.nullable(),
});
export type TaskDoneResponse = z.infer<typeof TaskDoneResponse>;

/**
 * `POST /api/staff/tasks/:id/fail` (UC-08 alternative flow, TL 2026-10-12): a `provision` task
 * that failed, with the reason (`validation_failed` for other kinds). The task stays open for
 * the next attempt (UC-24), the entitlement stays `pending` with provisioning `failed` and the
 * reason as its error; `provisioning.failed` goes to the timeline. Completing the task later
 * clears the error.
 */
export const TaskFailRequest = z.object({
  reason: Reason,
});
export type TaskFailRequest = z.infer<typeof TaskFailRequest>;

/** The task, still open, and the entitlement with the failure. */
export const TaskFailResponse = TaskDoneResponse;
export type TaskFailResponse = z.infer<typeof TaskFailResponse>;

/**
 * `POST /api/staff/entitlements/:id/key/rotate` (UC-31, AP-B8.5). Replaces the active key: the
 * worker creates the successor at OpenRouter with the entitlement's monthly limit and locks the
 * old one (`key.rotate`, ADR-119); the answer still shows the old key until the job ran. The
 * reason goes to the timeline with the staff member as actor and onto the old key's revocation.
 * Rejected without an active key with `conflict` and `key_missing` (ADR-085).
 */
export const KeyRotateRequest = z.object({
  reason: Reason,
});
export type KeyRotateRequest = z.infer<typeof KeyRotateRequest>;

/**
 * `POST /api/staff/entitlements/:id/revoke` (UC-17 by hand, AP-B8.5). Ends a living entitlement
 * with a mandatory reason: the key is locked at OpenRouter by the job `key.revoke` (ADR-119),
 * a provisioned resource gets the `remove` task (R-15). Rejected for an ended one with `conflict`
 * and `entitlement_not_live` (ADR-085).
 */
export const EntitlementRevokeRequest = z.object({
  reason: Reason,
});
export type EntitlementRevokeRequest = z.infer<typeof EntitlementRevokeRequest>;

/**
 * `POST /api/staff/entitlements/:id/terminate` (UC-16, ADR-137): support terminates a living
 * entitlement with a mandatory reason, effective at once; date and notice are agreed with the
 * customer outside the platform (K.1). The entitlement becomes `cancelled` with the activity
 * `entitlement.cancelled`; otherwise as at the revocation: the key locked by `key.revoke`
 * (ADR-119 H.3), the usage up to that day still fetched, a provisioned resource gets the
 * `remove` task, an open provisioning is cancelled. Rejected for an ended one with `conflict`
 * and `entitlement_not_live` (ADR-085).
 */
export const EntitlementTerminateRequest = z.object({
  reason: Reason,
});
export type EntitlementTerminateRequest = z.infer<typeof EntitlementTerminateRequest>;

/**
 * `PUT /api/staff/entitlements/:id/monthly-limit` (ADR-160 point F12.4-3, AP-B14-13): support and
 * admin set a customer's monthly limit in USD (ADR-058) with a mandatory reason. The same check of
 * `limit.set`, the same sum rule R-04 and the same way to the key at OpenRouter as the customer's
 * `PUT /api/entitlements/:id/monthly-limit` (UC-10, ADR-087, ADR-152), so the same refusals:
 * `limit_exceeded` with `credit_limit_exceeded` and `CreditLimitExceededDetails`; `forbidden` with
 * `customer_suspended` or `customer_not_approved`; `conflict` with `no_ai_access`,
 * `entitlement_not_live` or `demo_limit_fixed`. A customer's session gets `forbidden` and
 * `role_required`, an unknown entitlement `404`. The activity `monthly_limit.changed` names the team
 * and carries the reason; the customer reads both in the history, the team without a name
 * (ADR-117). An unchanged limit writes nothing.
 */
export const StaffMonthlyLimitRequest = MonthlyLimitRequest.extend({
  reason: Reason,
});
export type StaffMonthlyLimitRequest = z.infer<typeof StaffMonthlyLimitRequest>;

/**
 * The answer: the entitlement as the file shows it, with its `actions`, live usage and available
 * amount (`StaffEntitlement`), and as the customer's answer the credit limit, the sum of the living
 * monthly limits and the rest (`MonthlyLimitResponse`).
 */
export const StaffMonthlyLimitResponse = MonthlyLimitResponse.extend({
  entitlement: StaffEntitlement,
});
export type StaffMonthlyLimitResponse = z.infer<typeof StaffMonthlyLimitResponse>;

// ── keys ─────────────────────────────────────────────────────────────────────────

/**
 * `POST /api/staff/entitlements/:id/key/reveal` (UC-08). The plaintext, once per call; every
 * call is an activity `ai_key.revealed` in the customer's timeline (ADR-043). Customers never
 * see it (R-13). Without an active key `key_missing`; without the server key configured (until
 * AP-B9) `500 internal` (H-10). The answer is not cached (`Cache-Control: no-store`).
 */
export const KeyRevealResponse = z.object({
  key: z.string().min(1),
  revealedAt: IsoDateTime,
});
export type KeyRevealResponse = z.infer<typeof KeyRevealResponse>;

// ── usage reports, corrections, invoices ─────────────────────────────────────────

/** One row of the report list; the content comes with the single report. */
export const UsageReportSummary = z.object({
  id: Uuid,
  customerId: Uuid,
  customerName: z.string(),
  period: Period,
  /** billable usage plus corrections, what the invoice covers */
  openAmount: Money,
  /** demo usage, shown and never invoiced */
  nonBillableAmount: NonNegativeMoney,
  createdAt: IsoDateTime,
  frozenAt: IsoDateTime.nullable(),
  /** when the invoice was booked (M13); null until then */
  invoicedAt: IsoDateTime.nullable(),
  /**
   * ADR-075, ADR-102: `report.freeze` is refused with `period_invoiced` or `report_frozen` once
   * closed, `invoice.book` with `period_invoiced` once the month is invoiced. `correction.book`
   * is refused by role only: at a frozen report the API books into the first open month
   * (ADR-138 point 3). What depends on the input stays a refusal after sending. `UsageReport`
   * itself has no actions
   */
  actions: actionsOf("report.freeze", "correction.book", "invoice.book"),
});
export type UsageReportSummary = z.infer<typeof UsageReportSummary>;

/** `GET /api/staff/usage/reports`, newest period first. */
export const StaffUsageReportsQuery = PageQuery.extend({
  customerId: Uuid.optional(),
  period: Period.optional(),
});
export type StaffUsageReportsQuery = z.infer<typeof StaffUsageReportsQuery>;

export const StaffUsageReportsResponse = paged(UsageReportSummary);
export type StaffUsageReportsResponse = z.infer<typeof StaffUsageReportsResponse>;

/** `POST /api/staff/usage/reports/:id/freeze` (UC-13, UC-14): the report, now frozen. */
export const UsageReportFreezeResponse = UsageReport;

/**
 * `POST /api/staff/ledger/corrections` (UC-14, UC-15). A signed amount with a mandatory reason,
 * optionally for one product, without usage items (TL, 2026-10-12), at the report of the month it
 * corrects (`period`; 404 without one). Once that month is frozen, also by its invoice, the API
 * books into the first month that is not and names it in the answer: `period` of the entry is the
 * booked month, `correctedPeriod` this one (ADR-138 point 3). The original usage entry stays, the
 * correction is its own entry (R-05).
 */
export const CorrectionRequest = z.object({
  customerId: Uuid,
  /** the month corrected, the report's */
  period: Period,
  /** an entitlement of the customer; of a demo, the correction is not billable (R-07) */
  entitlementId: Uuid.optional(),
  /**
   * signed; a refund is negative. In the ledger's currency only, like the credit limit (ADR-058,
   * GC1 of the review 2026-10-30): the core books the micros as USD, so EUR would be booked as USD
   */
  amount: Money.refine((m) => !/^-?0+$/.test(m.micros), {
    message: "amount must not be zero",
    path: ["micros"],
  }).refine(inLedgerCurrency, ledgerCurrencyIssue),
  reason: Reason,
});
export type CorrectionRequest = z.infer<typeof CorrectionRequest>;

/**
 * `POST /api/staff/ledger/invoices` (UC-13, UC-16). Books minus the open amount of the period
 * over all billable entries, positive as a credit note when the corrections exceed the usage, and
 * freezes the report if it is not yet; once (`period_invoiced`). A later correction of the month
 * goes into the first open month (ADR-138 point 3).
 */
export const InvoiceRequest = z.object({
  customerId: Uuid,
  period: Period,
  invoiceReference: z.string().trim().min(1).max(100),
});
export type InvoiceRequest = z.infer<typeof InvoiceRequest>;

/** Both ledger routes answer with the entry they booked. */
export const LedgerEntryResponse = LedgerEntry;

/**
 * `GET /api/staff/customers/:id/ledger` (UC-15, ADR-151): the customer's ledger for
 * the file, newest entry first, by the month it is booked into (`period`) and by type, paged.
 */
export const StaffLedgerQuery = PageQuery.extend({
  period: Period.optional(),
  type: LedgerType.optional(),
});
export type StaffLedgerQuery = z.infer<typeof StaffLedgerQuery>;

/**
 * An entry of the ledger as the customer file shows it: the entry itself, and for reading the
 * product of its entitlement, the day and model of its usage item, the staff member's name and the
 * ledger's balance after it. Every entry is one row, a usage item's included (OFFEN 2 of the draft).
 */
export const StaffLedgerEntry = LedgerEntry.extend({
  /** the product of the entry's entitlement; null without one (an invoice, a correction for no product) */
  product: OrderProduct.nullable(),
  /** demo usage is booked and never invoiced (R-07); null without an entitlement */
  entitlementKind: EntitlementKind.nullable(),
  /** day (UTC) and model of the usage item; null on every type but `usage` */
  usage: z.object({ usageDate: IsoDate, model: z.string() }).nullable(),
  /** the staff member's name on corrections and invoices, as the timeline names actors; null otherwise */
  createdByName: z.string().nullable(),
  /**
   * The sum of the customer's billable entries up to and including this one, in the order of
   * booking (`createdAt`, then `id`): the open amount right after it, so the newest entry's is the
   * open amount of the file's head (07 §3.4). A filter does not change it; a demo entry leaves it
   * as it was. Signed: a credit larger than the usage makes it negative.
   */
  balanceAfter: Money,
});
export type StaffLedgerEntry = z.infer<typeof StaffLedgerEntry>;

export const StaffLedgerResponse = paged(StaffLedgerEntry);
export type StaffLedgerResponse = z.infer<typeof StaffLedgerResponse>;

// ── products, terms (UC-26, AP-B8.4) ─────────────────────────────────────────────

/**
 * All terms of a product as they stand or stood (UC-26): the fields of `PUT …/terms`, none left
 * out, so a change of the history reads on its own; `StaffProduct` carries the same. Amounts in
 * micro units as strings like everywhere.
 */
export const StaffProductTerms = z.object({
  billingModel: BillingModel,
  /** how the product is set up, it pre-fills the resource (ADR-144 point 1) */
  provider: ProviderType,
  /**
   * runs over the platform's AI keys: key, monthly limit, its share of the credit limit (ADR-144);
   * independent of the provider. A state of the history from before 0020 reads as
   * `provider = ai_cockpit`
   */
  aiAccess: z.boolean(),
  /**
   * sold on request only (ADR-112, ADR-147 point 1): no order and no demo, whatever the price and
   * demo say. The platform's since 0021; dataCycle's classification pre-fills it once, when the
   * sync creates the product. A state of the history from before 0021 reads as the product today
   */
  inquiryOnly: z.boolean(),
  /** the stored display price, also the placeholder of a product on request (ADR-063) */
  price: Money.nullable(),
  /** proposed monthly limit at purchase (F-46), USD (ADR-058) */
  defaultMonthlyLimit: NonNegativeMoney,
  /** the demo offer, also the placeholder of a product on request (UC-06, ADR-053) */
  demo: z
    .object({
      monthlyLimit: NonNegativeMoney,
      durationDays: Count.min(1),
    })
    .nullable(),
  /**
   * in the catalogue: a published product stands there only while active, one on request too
   * (ADR-146); inactive takes it out for customers, guests and the team's orders
   */
  active: z.boolean(),
});
export type StaffProductTerms = z.infer<typeof StaffProductTerms>;

/**
 * How a product is sold (ADR-147 point 1): with a price, orderable with "Preis auf Anfrage", or on
 * request only. Stored as `inquiry_only` and the display price; dataCycle states it with its two
 * classifications (`DC_INQUIRY_CONCEPT`, `DC_PRICE_ON_REQUEST_CONCEPT`).
 */
export const ProductSale = z.enum(["priced", "price_on_request", "inquiry_only"]);
export type ProductSale = z.infer<typeof ProductSale>;

/**
 * One product as the admin sees it (UC-26): identity and family from dataCycle, the terms the
 * platform owns. Every product of the catalogue sync is listed, unpublished ones included —
 * that is where their care begins (the sync creates them inactive with placeholder terms).
 * `published` and `family` are read-only; their source is dataCycle (ADR-103 point 3, ADR-112
 * point 3). Unlike the catalogue, `price` and `demo` show what is stored, also the placeholders
 * of a product on request.
 */
export const StaffProduct = z
  .object({
    id: Uuid,
    /** `@id` of the product in dataCycle, the link to its texts (ADR-054) */
    datacycleId: Uuid,
    slug: z.string().min(1),
    name: z.string().min(1),
    /** the family from dataCycle's "Produkttypen", null without one (ADR-112) */
    family: ProductFamily.nullable(),
    /** dataCycle publishes the product; the catalogue shows it only then (ADR-103 point 3) */
    published: z.boolean(),
    /** the platform's sale, derived by the API from `inquiryOnly` and the price (ADR-147 point 6) */
    sale: ProductSale,
    /**
     * dataCycle's sale where it differs from the platform's, null where they agree (ADR-147 point
     * 3); the portal shows it as a hint. `changedAt` is the newest change of dataCycle's sale in
     * its history, null when it has stood so since the first state the sync kept
     */
    datacycleSale: z.object({ sale: ProductSale, changedAt: IsoDateTime.nullable() }).nullable(),
  })
  // the terms the platform owns, the same fields as a state of the history (RN12)
  .extend(StaffProductTerms.shape)
  .extend({
    /**
     * ADR-075: `product.configure` decided by the role alone, whatever the product's state;
     * `product.delete` by the role and the product's state, `product_in_use` before
     * `product_published` (ADR-148 point 2)
     */
    actions: actionsOf("product.configure", "product.delete"),
  });
export type StaffProduct = z.infer<typeof StaffProduct>;

/** `GET /api/staff/products`. Not paged: like the catalogue, a handful of products (07 §2.2). */
export const StaffProductListResponse = z.object({
  items: z.array(StaffProduct),
});
export type StaffProductListResponse = z.infer<typeof StaffProductListResponse>;

/**
 * One change of a product's terms (ADR-125 addendum 2026-10-16): who changed them, when, and all
 * terms before and after. Kept for good; products belong to no customer, so it is no activity.
 */
export const StaffProductTermsChange = z.object({
  id: Uuid,
  productId: Uuid,
  changedAt: IsoDateTime,
  /** the staff member; `name` null without a display name */
  changedBy: z.object({ userId: Uuid, name: z.string().nullable() }),
  before: StaffProductTerms,
  after: StaffProductTerms,
});
export type StaffProductTermsChange = z.infer<typeof StaffProductTermsChange>;

/** `GET /api/staff/products/:id/history` (UC-26): the product's changes, newest first, paged. */
export const StaffProductHistoryQuery = PageQuery;
export type StaffProductHistoryQuery = z.infer<typeof StaffProductHistoryQuery>;
export const StaffProductHistoryResponse = paged(StaffProductTermsChange);
export type StaffProductHistoryResponse = z.infer<typeof StaffProductHistoryResponse>;

/**
 * `PUT /api/staff/products/:id/terms` (UC-26): the terms the platform owns, each field left out
 * keeps its value. Unchanged terms write nothing. What is impossible (negative amounts, a
 * product of billing model `demo` without its demo terms, a sale that does not fit the price) is
 * refused with `validation_failed` after sending (ADR-075). `published` and `family` are
 * dataCycle's and cannot be set; the way of sale is set through `sale` (ADR-147).
 */
export const StaffProductTermsRequest = z.object({
  billingModel: BillingModel.optional(),
  provider: ProviderType.optional(),
  /** AI access (ADR-144); like every term it holds for new orders only (ADR-130) */
  aiAccess: z.boolean().optional(),
  /**
   * the sale (ADR-147 point 6): `priced` needs a price, sent or stored (else `400` at `price`);
   * `price_on_request` removes the price and refuses one sent with it (`400` at `price`);
   * `inquiry_only` keeps price and demo as stored, placeholders that reach nobody (point 5)
   */
  sale: ProductSale.optional(),
  /** the display price with its currency (ADR-063); never null, no price is `sale: "price_on_request"` */
  price: NonNegativeMoney.optional(),
  defaultMonthlyLimit: NonNegativeMoney.refine(inLedgerCurrency, ledgerCurrencyIssue).optional(),
  /** null removes the demo offer, limit and duration together (UC-06) */
  demo: z
    .object({
      monthlyLimit: NonNegativeMoney.refine(inLedgerCurrency, ledgerCurrencyIssue),
      durationDays: Count.min(1),
    })
    .nullable()
    .optional(),
  /** into the catalogue or out of it */
  active: z.boolean().optional(),
});
export type StaffProductTermsRequest = z.infer<typeof StaffProductTermsRequest>;

/**
 * A state of a product as the catalogue sync kept it (ADR-147 point 4): what dataCycle owns,
 * without the order (OFFEN 1). `datacycleInquiryOnly` and `datacyclePriceOnRequest` are the two
 * classifications as dataCycle states them, not the platform's sale.
 */
export const StaffProductSyncState = z.object({
  name: z.string().min(1),
  slug: z.string().min(1),
  vendor: z.string().min(1),
  family: ProductFamily.nullable(),
  published: z.boolean(),
  datacycleInquiryOnly: z.boolean(),
  datacyclePriceOnRequest: z.boolean(),
});
export type StaffProductSyncState = z.infer<typeof StaffProductSyncState>;

/**
 * One change the catalogue sync wrote to a product, or the first state it kept of it (`before`
 * null); kept for good like the history of the terms (ADR-147 point 4).
 */
export const StaffProductSyncChange = z.object({
  id: Uuid,
  productId: Uuid,
  changedAt: IsoDateTime,
  before: StaffProductSyncState.nullable(),
  after: StaffProductSyncState,
});
export type StaffProductSyncChange = z.infer<typeof StaffProductSyncChange>;

/** `GET /api/staff/products/:id/sync-history` (UC-26): dataCycle's changes, newest first, paged. */
export const StaffProductSyncHistoryQuery = PageQuery;
export type StaffProductSyncHistoryQuery = z.infer<typeof StaffProductSyncHistoryQuery>;
export const StaffProductSyncHistoryResponse = paged(StaffProductSyncChange);
export type StaffProductSyncHistoryResponse = z.infer<typeof StaffProductSyncHistoryResponse>;
